<template>
    <v-card class="main-card">
        <v-card-title>
            <div class="main_title">
                <h6><strong>{{ title }}</strong></h6>
                <button @click="showNewQuestionDialog = true" class="faccess_button">Crear pregunta</button>
            </div>
        </v-card-title>

        <v-card-text>
            <!-- Empty state -->
            <v-alert v-if="!questions.length" type="info" variant="tonal" density="comfortable">
                Aún no hay preguntas creadas para esta funcionalidad.
            </v-alert>

            <!-- Questions list -->
            <v-row v-else dense style="gap: 1rem; display: flex; flex-direction: column;">
                <v-col v-for="question in questions" :key="question.id" cols="12">
                    <v-card class="question-card" variant="outlined">
                        <v-card-title class="question-title-container">
                            <span class="question-title">
                                {{ question.title }}
                            </span>

                            <div class="question-actions">
                                <v-btn size="small" variant="text" color="primary" @click="editQuestion(question)"
                                    title="Editar pregunta">
                                    Editar
                                </v-btn>

                                <v-btn size="small" variant="text" color="error"
                                    @click="confirmDeleteQuestion(question)" title="Eliminar pregunta">
                                    Eliminar
                                </v-btn>
                            </div>
                        </v-card-title>


                        <v-card-text class="question-content">
                            <p v-if="question.content" class="question-text">
                                {{ question.content }}
                            </p>

                            <div v-if="question.videoUrl" class="video-wrapper">
                                <video :src="question.videoUrl" controls class="question-video" />
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-card-text>

        <v-dialog v-model="showDeleteConfirm" max-width="420">
            <v-card>
                <v-card-title class="text-h6">
                    Eliminar pregunta
                </v-card-title>

                <v-card-text>
                    ¿Estás seguro de que deseas eliminar esta pregunta?
                    <br />
                    <strong>Esta acción no se puede deshacer.</strong>
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <v-spacer />

                    <v-btn variant="text" @click="cancelDelete">
                        Cancelar
                    </v-btn>

                    <v-btn color="error" @click="deleteQuestion">
                        Eliminar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>


        <v-dialog v-model="showNewQuestionDialog" max-width="600" persistent>
            <v-card class="create-question-card">
                <!-- Header -->
                <v-card-title class="dialog-header">
                    <div>
                        <h3 class="dialog-title">
                            {{ isEditing ? 'Editar pregunta' : 'Crear pregunta' }}
                        </h3>
                        <p class="dialog-subtitle">
                            Completa la información de ayuda
                        </p>
                    </div>

                    <v-btn icon variant="text" :disabled="saving" @click="closeNewQuestionDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-divider />

                <!-- Content -->
                <form @submit.prevent="createQuestion">
                    <v-card-text class="dialog-content">
                        <v-text-field v-model="newQuestion.title" label="Título" variant="outlined"
                            density="comfortable" hide-details required />

                        <v-textarea v-model="newQuestion.content" label="Contenido (opcional)" variant="outlined"
                            density="comfortable" auto-grow rows="3" />

                        <div class="upload-container" :class="{ 'upload-filled': newQuestion.videoPreview }">
                            <template v-if="newQuestion.videoPreview">
                                <div class="video-preview-wrapper">
                                    <video :src="newQuestion.videoPreview" controls class="video-preview" />
                                    <span @click="removeVideo" class="preview-remove-icon"
                                        :class="{ 'preview-remove-disabled': saving }">
                                        <delete-icon color="white"></delete-icon>
                                    </span>
                                </div>
                            </template>

                            <template v-else>
                                <upload-icon class="upload-icon" />
                                <div class="upload-text">
                                    <strong>Subir video (opcional)</strong>
                                    <span>Formato .mp4</span>
                                </div>
                            </template>

                            <input ref="uploadInput" type="file" class="upload-input" accept="video/mp4"
                                @change="onVideoSelected" :disabled="!!newQuestion.videoPreview || saving" />

                        </div>


                        <v-alert v-if="showQuestionError" type="error" variant="tonal" density="compact">
                            Debes ingresar contenido o un video.
                        </v-alert>

                        <v-alert v-if="uploadError" type="error" variant="tonal" density="compact">
                            {{ uploadError }}
                        </v-alert>
                    </v-card-text>

                    <v-divider />

                    <!-- Actions -->
                    <v-card-actions class="dialog-actions">
                        <v-btn variant="text" :disabled="saving" @click="closeNewQuestionDialog">
                            Cancelar
                        </v-btn>

                        <v-btn color="primary" :disabled="!canCreateQuestion || saving"
                            @click="isEditing ? updateQuestion() : createQuestion()">
                            <v-progress-circular v-if="saving" indeterminate size="18" width="2" color="white" />
                            <span v-else>{{ isEditing ? 'Guardar cambios' : 'Crear pregunta' }}</span>
                        </v-btn>
                    </v-card-actions>
                </form>
            </v-card>
        </v-dialog>

    </v-card>
</template>
<script>

export default {
    data() {
        return {
            featureId: this.$route.params.id,
            title: '',
            showNewQuestionDialog: false,
            newQuestion: {
                title: '',
                content: '',
                video: null,
                videoPreview: null,
                hadBackendVideo: false,
                removeVideo: false
            },
            showQuestionError: false,
            uploadError: null,
            saving: false,
            questions: [],
            editingQuestionId: null,
            showDeleteConfirm: false,
            questionToDelete: null,

        };
    },
    methods: {
        async initialize() {
            await this.fetchFeatureInfo();
            await this.fetchFeatureQuestions();
        },
        async fetchFeatureInfo() {
            try {
                // Fetch questions based on featureId
                const response = await this.$http.get(`features/${this.featureId}`);
                this.title = response.data.data.attributes.name;
            } catch (error) {
                console.error('Error fetching feature questions:', error);
            }
        },
        async fetchFeatureQuestions() {
            try {
                const response = await this.$http.get(`help_questions`, {
                    params: {
                        feature_id: this.featureId,
                    }
                });
                this.questions = response.data.data.map(q => ({
                    id: q.id,
                    title: q.attributes.title,
                    content: q.attributes.content,
                    videoUrl: q.attributes.video_url,
                }));
            } catch (error) {
                console.error('Error fetching feature questions:', error);
            }
        },
        
        onVideoSelected(event) {
            const file = event.target.files[0]
            if (!file) return

            if (file.type !== 'video/mp4') {
                alert('Solo se permiten videos .mp4')
                event.target.value = ''
                return
            }

            this.newQuestion.video = file
            this.newQuestion.videoPreview = URL.createObjectURL(file)

            // Si sube nuevo video, cancelamos eliminación
            this.newQuestion.removeVideo = false
        },

        editQuestion(question) {
            this.editingQuestionId = question.id

            this.newQuestion = {
                title: question.title,
                content: question.content,
                video: null,
                videoPreview: question.videoUrl || null,
                hadBackendVideo: !!question.videoUrl,
                removeVideo: false
            }

            this.showNewQuestionDialog = true
        },

        async createQuestion() {
            if (!this.canCreateQuestion) {
                this.showQuestionError = true
                return
            }

            this.uploadError = null
            this.saving = true
        
            const payload = {
                feature_id: this.featureId,
                title: this.newQuestion.title,
                content: this.newQuestion.content || undefined,
            }
        
            try {
                if (this.newQuestion.video) {
                    payload.video = await this.uploadVideo(this.newQuestion.video)
                }
        
                await this.$http.post(
                    'help_questions',
                    { data: this.encrypt(payload).toString() },
                    {
                        headers: {
                            "X-Device-ID": this.getDeviceId(),
                            "Authorization": 'Bearer ' + this.getUserToken(),
                        }
                    }
                )
        
                await this.fetchFeatureQuestions()
                this.closeNewQuestionDialog()
            } catch (error) {
                console.error('Error creating question:', error)
                this.uploadError = 'No se pudo crear la pregunta. Intenta de nuevo.'
            } finally {
                this.saving = false
            }
        },

        closeNewQuestionDialog() {
            this.showNewQuestionDialog = false
            this.showQuestionError = false
            this.uploadError = null

            if (this.newQuestion.videoPreview && this.newQuestion.videoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(this.newQuestion.videoPreview)
            }

            this.newQuestion = {
                title: '',
                content: '',
                video: null,
                videoPreview: null,
                hadBackendVideo: false,
                removeVideo: false
            }

            this.editingQuestionId = null

            if (this.$refs.uploadInput) {
                this.$refs.uploadInput.value = null
            }
        },

        async updateQuestion() {
            if (!this.canCreateQuestion) {
                this.showQuestionError = true
                return
            }

            this.uploadError = null
            this.saving = true

            const payload = {
                title: this.newQuestion.title,
                content: this.newQuestion.content || undefined,
            }

     
          try {

            if (this.newQuestion.video) {
                payload.video = await this.uploadVideo(this.newQuestion.video)   // new video wins
            } else if (this.newQuestion.removeVideo) {
                payload.video = null
            }
         

            
            await this.$http.put(
                    `help_questions/${this.editingQuestionId}`,
                    { data: this.encrypt(payload).toString() },
                    {
                        headers: {
                            "X-Device-ID": this.getDeviceId(),
                            "Authorization": 'Bearer ' + this.getUserToken(),
                        }
                    }
          )

            await this.fetchFeatureQuestions()
            this.closeNewQuestionDialog()
            } catch (error) {
                console.error('Error updating question:', error)
                this.uploadError = 'No se pudo actualizar la pregunta. Intenta de nuevo.'
            } finally {
                this.saving = false
            }
        },


        confirmDeleteQuestion(question) {
            this.questionToDelete = question
            this.showDeleteConfirm = true
        },
        cancelDelete() {
            this.showDeleteConfirm = false
            this.questionToDelete = null
      },
        async uploadVideo(file) {
          if (file.type !== 'video/mp4') {
              throw new Error('Unsupported video format: ' + file.type)
          }
      
          const response = await this.$http.post(
              'help_questions/presign',
              {
                  feature_id: this.featureId,
                  filename: file.name,
                  content_type: file.type,
              },
              {
                  headers: {
                      "X-Device-ID": this.getDeviceId(),
                      "Authorization": 'Bearer ' + this.getUserToken(),
                  }
              }
          )
      
          // upload file to s3
          const upload = await fetch(response.data.presigned_url, {
              method: 'PUT',
              headers: { 'Content-Type': file.type },
              body: file,
          })
          if (!upload.ok) throw new Error('S3 upload failed: ' + upload.status)
      
          return response.data.public_url
      },
        async deleteQuestion() {
            if (!this.questionToDelete) return

            try {
                await this.$http.delete(
                    `help_questions/${this.questionToDelete.id}`,
                    {
                        headers: {
                            "X-Device-ID": this.getDeviceId(),
                            "Authorization": 'Bearer ' + this.getUserToken(),
                        }
                    }
                )

                await this.fetchFeatureQuestions()
                this.cancelDelete()
            } catch (error) {
                console.error('Error deleting question:', error)
            }
        },

        removeVideo() {
            if (this.saving) return

            if (this.newQuestion.videoPreview && this.newQuestion.videoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(this.newQuestion.videoPreview)
            }

            this.newQuestion.video = null
            this.newQuestion.videoPreview = null

            // Si el video venía del backend, marcamos eliminación
            if (this.newQuestion.hadBackendVideo) {
                this.newQuestion.removeVideo = true
            }

            // Reactivar input
            if (this.$refs.uploadInput) {
                this.$refs.uploadInput.disabled = false
                this.$refs.uploadInput.value = null
            }
        },



    },
    computed: {
        canCreateQuestion() {
            return (
                this.newQuestion.title &&
                (this.newQuestion.content || this.newQuestion.video || this.newQuestion.videoPreview)
            )
        },
        isEditing() {
            return !!this.editingQuestionId
        },
    },
    mounted() {
        this.initialize();
    }
}
</script>

<style scoped>
.main_title {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.main-card {
    border-radius: 1rem;
}

.question-title-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
}

.question-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}



/* Upload base */
.upload-container {
    position: relative;
    width: 100%;
    min-height: 140px;
    border: 2px dashed #c7c7c7;
    border-radius: 14px;
    padding: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    cursor: pointer;
    transition: all 0.2s ease;
    background: #fafafa;
}

/* Hover */
.upload-container:hover {
    border-color: #6750a4;
    background: #6750a410;
}

/* When video is loaded */
.upload-container.upload-filled {
    border-style: solid;
    padding: 0.75rem;
}

/* Text & icon */
.upload-icon {
    font-size: 2.2rem;
    color: #6750a4;
    width: 150px;
}

.upload-text {
    display: flex;
    flex-direction: column;
    font-size: 0.85rem;
    color: #555;
}

.upload-text strong {
    font-weight: 600;
}

.upload-text span {
    font-size: 0.75rem;
    color: #888;
}

/* Invisible input */
.upload-input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
}

.upload-input:disabled {
    pointer-events: none;
}

/* Preview container */
.video-preview-wrapper {
    width: 100%;
    max-width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 14px;
    overflow: hidden;
    background: #000;
}

/* Video */
.video-preview {
    width: 100%;
    height: 100%;
    object-fit: contain;
    /* mantiene proporción */
    border-radius: inherit;
}

/* Mobile friendly */
@media (max-width: 600px) {
    .upload-container {
        min-height: 120px;
        padding: 1rem;
    }

    .video-preview {
        max-height: 200px;
    }
}

.question-card {
    border-radius: 14px;
    background: #3c3c3c36;
}

.question-title {
    font-weight: 600;
    font-size: 1rem;
}

.question-content {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.question-text {
    font-size: 0.9rem;
    color: #444;
    margin: 0;
}

.video-wrapper {
    width: 100%;
    max-width: 520px;
    /* tamaño del video en el listado */
    aspect-ratio: 16 / 9;
    /* relación de aspecto */
    border-radius: 12px;
    overflow: hidden;
    background: #000;
}

.question-video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    /* mantiene el aspecto sin recortar */
    border-radius: inherit;
}

.preview-remove-icon {
    position: absolute;
    right: 10px;
    top: 10px;
    width: 40px;
    height: 40px;
    background: red;
    padding: 10px;
    border-radius: 50%;
}

.preview-remove-disabled {
    opacity: 0.5;
    pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// src/components/views/feautres/FeatureQuestions.vue